import * as flexsearch from "flexsearch";
import _ from "lodash";

import { search as searchApi, workspaceResources as workspaceResourcesApi } from "apis-v2/callables";
import { presentations as presentationsApi } from "apis/callables";
import { PresentationPrivacyType, THUMBNAIL_SIZES } from "common/constants";
import { GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS, IPresentation, IPresentationMetadata, IPresentationStorageModel, ISlide, IWorkspacePermissionAccess, IWorkspaceResourcePresentation, IWorkspaceResourcePresentationTemplate, PaginatedFilter, PaginatedSort, PresentationActivityType, WorkspacePermissionSubjectType, WorkspaceResourceType } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import { withMaxConcurrency } from "common/utils/withMaxConcurrency";
import { appVersion } from "js/config";
import WorkspaceController, { getSlidesInPresentationsCount, getUserPermissionsToResourceFolder, getWorkspacePermissionsToResource } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { getPresentation, getPresentationPreloaded, getTemplateSlideData } from "js/core/models/presentation";
import { SharedTheme } from "js/core/models/sharedTheme";
import { Theme } from "js/core/models/theme";
import Thumbnails from "js/core/models/thumbnails";
import NotificationsService from "js/core/services/notifications";
import pusher from "js/core/services/pusher";
import themeManager from "js/core/themeManager";
import * as gDrive from "js/core/utilities/gDrive";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowErrorDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";

import { BasePresentationsPaneController, BasePresentationsPaneControllerState, IPresentationsPanePresentation } from "./BasePresentationsPaneController";
import { BASE_INITIAL_STATE, BaseWorkspaceResourcesControllerState } from "./BaseWorkspaceResourcesController";

const logger = getLogger(LogGroup.LIBRARY);

export class DirectPermissionRequiredError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "DirectPermissionRequiredError";
    }
}

export type PresentationLibraryPaneControllerState = BasePresentationsPaneControllerState<IWorkspaceResourcePresentation> & {
    viewType: "grid" | "list";
    libraryFilter: PresentationLibraryFilter | null;
    currentFolderPermissions: IWorkspacePermissionAccess;
    dirtyPresentationIds: string[];
}

/**
 * Legacy type
 */
export interface PresentationLibraryPresentation extends IPresentationMetadata {
    getThumbnailUrl: (size?: "small" | "large") => Promise<string>;
    dirty?: boolean;
}

export interface PresentationLibraryFilter {
    type: PresentationLibraryFilterType;
    folderId?: string;
    subFolderId?: string;
    presentationIds?: string[];
}

export enum PresentationLibraryFilterType {
    ALL_PRESENTATIONS = "all_presentations",
    RECENT = "recent",
    OWNED_BY_ME = "owned_by_me",
    SHARED_WITH_ME = "shared_with_me",
    VIEW_ONLY = "view_only",
    FOLDER = "folder",
    TEAM_FOLDER = "team_folder",
    TRASH = "trash",
    PRESENTATION_IDS = "presentation_ids"
}

const getInitialState = () => ({
    ...(BASE_INITIAL_STATE as BaseWorkspaceResourcesControllerState<IPresentationsPanePresentation, IWorkspaceResourcePresentation>),
    viewType: "grid",
    libraryFilter: null,
    currentFolderPermissions: GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS(),
    dirtyPresentationIds: []
} as PresentationLibraryPaneControllerState);

function getFiltersFromPresentationLibraryFilter(filter: PresentationLibraryFilter): PaginatedFilter[] {
    switch (filter.type) {
        case PresentationLibraryFilterType.PRESENTATION_IDS:
            return [{
                field: "resource.resourceId",
                value: filter.presentationIds ?? [],
                operator: "in"
            }, {
                field: "document.softDeletedAt",
                value: null,
                operator: "eq"
            }];
        case PresentationLibraryFilterType.TRASH:
            return [{
                field: "document.softDeletedAt",
                value: null,
                operator: "ne"
            }];
        case PresentationLibraryFilterType.OWNED_BY_ME:
            return [{
                field: "permission.owner",
                value: true,
                operator: "eq"
            }, {
                field: "document.softDeletedAt",
                value: null,
                operator: "eq"
            }];
        case PresentationLibraryFilterType.SHARED_WITH_ME:
            return [{
                field: "permission.owner",
                value: false,
                operator: "eq"
            }, {
                field: "document.softDeletedAt",
                value: null,
                operator: "eq"
            }];
        case PresentationLibraryFilterType.VIEW_ONLY:
            return [{
                field: "permission.write.object",
                value: false,
                operator: "eq"
            }, {
                field: "document.softDeletedAt",
                value: null,
                operator: "eq"
            }];
        case PresentationLibraryFilterType.FOLDER:
        case PresentationLibraryFilterType.TEAM_FOLDER:
            return [{
                field: "resource.folderIds",
                value: filter.subFolderId ?? filter.folderId,
                operator: "eq"
            }, {
                field: "document.softDeletedAt",
                value: null,
                operator: "eq"
            }];
        case PresentationLibraryFilterType.RECENT:
            const recentPresentationIds = AppController.user.getRecentPresentations().reverse();
            return [{
                field: "resource.resourceId",
                value: recentPresentationIds,
                operator: "in"
            }, {
                field: "document.softDeletedAt",
                value: null,
                operator: "eq"
            }];
        case PresentationLibraryFilterType.ALL_PRESENTATIONS:
        default:
            return [{
                field: "document.softDeletedAt",
                value: null,
                operator: "eq"
            }];
    }
}

async function createPresentationWithSlidesAndResource(options: {
    workspaceId: string;
    presentation: Partial<IPresentation>;
    theme?: typeof Theme | typeof SharedTheme;
    slides?: (Partial<ISlide> & { template_id: string })[];
}) {
    const {
        workspaceId,
        presentation: initialData,
        theme,
        slides = [],
    } = options;

    if (slides.length === 0 && Object.keys(initialData.slideRefs || {}).length === 0) {
        throw new Error("At least one slide is required");
    }

    const uid = auth().currentUser.uid;

    const playerSettingsDefaults = _.cloneDeep(WorkspaceController.workspace.player.settings);

    const presentationData: Partial<IPresentation> = {
        ...playerSettingsDefaults,
        ...initialData,
        sharedThemeId: null,
        userId: uid,
        isTemplate: false
    };

    if (theme && theme instanceof SharedTheme) {
        presentationData.sharedThemeId = theme.id;
    } else if (theme) {
        Object.assign(presentationData, themeManager.getPresentationUpdates(theme));
    }

    const slidesData: Partial<ISlide>[] = [];
    if (slides.length > 0) {
        await Promise.all(slides.map(async slideData => {
            slideData = await getTemplateSlideData(slideData.template_id, {
                ...slideData,
                version: appVersion
            });

            slidesData.push(slideData);
        }));
    }

    const presentation = await presentationsApi.createPresentationWithSlidesAndResource({
        workspaceId,
        presentation: presentationData,
        slides: slidesData
    });

    if (app.isMobileOrTablet) {
        NotificationsService.notifyOnPresentationGeneratedOnMobile(presentation.id)
            .catch(err => logger.error(err, "[PresentationLibraryPaneController] notifyOnPresentationGeneratedOnMobile failed", { presentationId: presentation.id }));
    }

    const presentationModel = await getPresentationPreloaded(presentation, workspaceId, { permission: "write", autoSync: true });

    const currentSlideCount = await getSlidesInPresentationsCount(workspaceId).catch(() => 0);
    const eventProps = {
        "presentation_id": presentationModel.id,
        "current_slide_count": currentSlideCount,
        "slides_created": slides.length,
        "workspace_id": workspaceId
    };
    trackActivity("Presentation", "New", null, null, eventProps, { audit: true });

    // Fire and forget
    (async () => {
        const user = AppController.user;
        if (user.get("isGDriveEnabled")) {
            const gDriveFolderId = new URL(window.location.href).searchParams.get("gDriveFolderId");
            gDrive.savePresentation(presentationModel.id, gDriveFolderId)
                .catch(err => {
                    logger.error(err, "PresentationLibraryPaneController gDrive.savePresentation() failed", { presentationId: presentationModel.id, gDriveFolderId });
                });
        }
    })().catch(err => {
        logger.error(err, "[PresentationLibraryPaneController] createPresentationWithSlidesAndResource() failed to save presentation to GDrive", { presentationId: presentationModel.id });
    });

    return presentationModel;
}

async function movePresentationToLibraryFilter(presentation: { id: string }, libraryFilter: PresentationLibraryFilter) {
    if (libraryFilter.type !== PresentationLibraryFilterType.TEAM_FOLDER && libraryFilter.type !== PresentationLibraryFilterType.FOLDER) {
        return;
    }

    const folderId = libraryFilter.subFolderId ?? libraryFilter.folderId;

    try {
        await WorkspaceController.updateWorkspaceResourceFolderResources({
            folderId,
            update: {
                add: [{
                    resourceId: presentation.id,
                    resourceType: WorkspaceResourceType.PRESENTATION,
                    permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                }],
            }
        });
    } catch (err) {
        logger.error(err, "PresentationLibraryPaneController movePresentationToLibraryFilter() failed", { presentationId: presentation.id, libraryFilter });

        ShowErrorDialog({
            title: "Oops!",
            message: "Failed to move presentation to folder",
            acceptCallback: () => { }
        });
    }
}

export class PresentationLibraryPaneController extends BasePresentationsPaneController<IWorkspaceResourcePresentation, PresentationLibraryPaneControllerState> {
    constructor() {
        super(getInitialState());
    }

    protected get _resourceTypes(): WorkspaceResourceType[] {
        return [WorkspaceResourceType.PRESENTATION];
    }

    protected async _fetchSearchIndex({ locale, workspaceId }: { locale: string, workspaceId: string }): Promise<{ indexConfig: flexsearch.DocumentOptions<any>, indexFileReadUrl: string }> {
        return searchApi.getPresentationsSearchIndex({ workspaceId, locale });
    }

    protected async _setCurrentFolderPermissions() {
        try {
            const { workspaceId } = this._state;

            const libraryFilter = this.libraryFilter;
            if (libraryFilter.type !== PresentationLibraryFilterType.TEAM_FOLDER) {
                await this._updateState({ currentFolderPermissions: GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS() });
                return;
            }

            const permissions = await getUserPermissionsToResourceFolder({
                workspaceId,
                folderId: libraryFilter.folderId
            });
            await this._updateState({ currentFolderPermissions: permissions });
        } catch (err) {
            logger.error(err, "[PresentationLibraryPaneController] _setCurrentFolderPermissions()");
            await this._updateState({ currentFolderPermissions: GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS() });
        }
    }

    protected _stateDidUpdate(prevState: PresentationLibraryPaneControllerState) {
        super._stateDidUpdate(prevState);

        if (getObjectHash(prevState.filters) !== getObjectHash(this._state.filters)) {
            this._setCurrentFolderPermissions();
        }

        if (prevState.viewType !== this._state.viewType || getObjectHash(prevState.sort) !== getObjectHash(this._state.sort)) {
            this._updateUserLibrarySettings()
                .catch(err => logger.error(err, "[PresentationLibraryPaneController] _updateUserLibrarySettings()"));
        }
    }

    protected async _updateUserLibrarySettings() {
        if (!this.initialized) {
            return;
        }

        const { viewType, sort } = this._state;
        const user = AppController.user;

        const librarySettings = _.cloneDeep(user.get("librarySettings") ?? {});
        librarySettings.viewType = viewType;

        const primarySort = sort[0];
        if (primarySort?.field && primarySort.field !== "relevance") {
            librarySettings.sortField = primarySort.field;
            librarySettings.sortReverse = primarySort.direction === "desc";
        }

        user.update({ librarySettings });
        await user.updatePromise;
    }

    public override async initialize({ viewType, sort, filters, libraryFilter, workspaceId }: { viewType: "grid" | "list", sort?: PaginatedSort[], filters?: PaginatedFilter[], libraryFilter?: PresentationLibraryFilter, workspaceId: string }) {
        await super.initialize({
            sort,
            filters: libraryFilter ? getFiltersFromPresentationLibraryFilter(libraryFilter) : (filters ?? []),
            libraryFilter,
            workspaceId,
            viewType
        });
    }

    public override async reset(initialState: PresentationLibraryPaneControllerState = getInitialState()) {
        return super.reset(initialState);
    }

    public setViewType(viewType: "grid" | "list") {
        return this._runSequentially(() => this._updateState({ viewType }));
    }

    /**
     * Returns the filter in the legacy format so it can be directly used in the old UI.
     * Must be removed after the UI is refactored.
     */
    public get libraryFilter(): PresentationLibraryFilter {
        const { libraryFilter } = this._state;
        return libraryFilter ?? { type: PresentationLibraryFilterType.ALL_PRESENTATIONS };
    }

    /**
     * Sets the filter from the legacy format so it can be directly used by the old UI.
     * Must be removed after the UI is refactored.
     */
    public async setLibraryFilter(libraryFilter: PresentationLibraryFilter) {
        return this._updateState({ libraryFilter, filters: getFiltersFromPresentationLibraryFilter(libraryFilter) });
    }

    public async setFilters(filters: PaginatedFilter[]) {
        return this._updateState({ filters, libraryFilter: null });
    }

    public get currentFolderPermissions(): IWorkspacePermissionAccess {
        return this._state.currentFolderPermissions;
    }

    /**
     * Returns the data in the legacy format so it can be directly used in the old UI.
     * Must be removed after the UI is refactored.
     */
    public get legacyData(): PresentationLibraryPresentation[] {
        const { dirtyPresentationIds } = this._state;

        return this._state.data.map(record => {
            const { resource, document: presentation } = record;

            const slideRefs = presentation.slideRefs ?? {};
            const slideIds = Object.entries(slideRefs)
                .sort(([, slideIndexA], [, slideIndexB]) => slideIndexA - slideIndexB)
                .map(([slideId]) => slideId);
            const owner = WorkspaceController.workspaceUsers.find(user => user.uid === presentation.userId);
            const lastActivity = resource.metadata.lastActivityByType?.[PresentationActivityType.EDITED];
            const lastActivityUser = lastActivity?.userId ? WorkspaceController.workspaceUsers.find(user => user.uid === lastActivity.userId) : null;
            const isTemplate = false;

            const metadata: IPresentationMetadata = {
                id: presentation.id,
                name: presentation.name,
                createdAt: presentation.createdAt,
                modifiedAt: Math.max(presentation.modifiedAt, presentation.finishedEditingAt ?? 0),
                slideIds,
                firstSlideModifiedAt: presentation.firstSlideModifiedAt,
                slideCount: slideIds.length,
                trashedAt: presentation.softDeletedAt,
                permissions: {
                    owner: record.permission.owner,
                    write: record.permission.write.object,
                    read: record.permission.read.object,
                },
                workspaceId: resource.workspaceId,
                isTemplate,
                isPublished: false,
                ownerUid: presentation.userId ?? "",
                ownerName: owner?.displayName || owner?.email || "",
                ownerPhotoURL: owner?.photoURL || "",
                ratedByUsers: [],
                metadataGeneratedAt: Date.now(),
                isFromRecommendedTemplate: !!presentation.metadata?.isFromRecommendedTemplate,
                finishedEditingAt: presentation.finishedEditingAt,
                ...(lastActivity && {
                    lastActivity: {
                        userId: lastActivity.userId,
                        userName: lastActivityUser?.displayName || lastActivityUser?.email || "",
                        activity: lastActivity.activity,
                        createdAt: lastActivity.createdAt,
                    }
                })
            };

            return {
                ...metadata,
                dirty: dirtyPresentationIds.includes(presentation.id),
                getThumbnailUrl: (size: "small" | "large" = "small") => Thumbnails.getSignedUrlAndLoad(slideIds[0], presentation.firstSlideModifiedAt, presentation.id, THUMBNAIL_SIZES[size].suffix, 0, false)
            } as PresentationLibraryPresentation;
        });
    }

    public async forceRefreshPresentations(presentationIds: string[]) {
        await this._runSequentially(async () => {
            if (!this._state.initialized) {
                return;
            }

            await Promise.all(presentationIds.map(presentationId =>
                this._forceRefreshResource(presentationId)
            ));

            await this._updateState(state => ({
                ...state,
                dirtyPresentationIds: state.dirtyPresentationIds.filter(id => !presentationIds.includes(id))
            }));
        });
    }

    public async markPresentationsDirty(presentationIds: string[]) {
        await this._updateState(state => ({
            ...state,
            dirtyPresentationIds: _.uniq([...state.dirtyPresentationIds, ...presentationIds])
        }));
    }

    public async createPresentation(options: {
        workspaceId: string;
        theme?: typeof Theme | typeof SharedTheme;
        name?: string;
        isTemplate?: boolean;
        slides?: (Partial<ISlide> & { template_id: string })[];
        metadata?: Record<string, any>;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const {
            workspaceId,
            theme = ds.builtInThemes.sample(),
            slides = [],
            isTemplate = false,
            metadata = {},
            libraryFilter
        } = options;
        // Leave the name undefined when none is supplied — the server assigns the next
        // available "Untitled #N" for the workspace so the count is always accurate.
        const name = options.name;

        if (slides.length === 0) {
            slides.push({ template_id: "title" });
        }

        const initialData: Pick<IPresentation, "orgId" | "metadata" | "createdAt" | "modifiedAt" | "isTemplate"> & { name?: string } = {
            name,
            orgId: workspaceId,
            metadata,
            createdAt: Date.now(),
            modifiedAt: Date.now(),
            isTemplate
        };

        const presentation = await createPresentationWithSlidesAndResource({
            workspaceId,
            presentation: initialData,
            theme,
            slides
        });

        if (libraryFilter) {
            await movePresentationToLibraryFilter(presentation, libraryFilter);
        }

        // Ensure the resource is added to state (in case the real time update comes with a delay)
        this._runSequentially(() => {
            if (!this._state.initialized) {
                return;
            }
            return this._forceRefreshResource(presentation.id);
        }).catch(err => {
            logger.error(err, "[PresentationLibraryPaneController] createPresentation() failed to refresh resource", { presentationId: presentation.id });
        });

        return presentation;
    }

    public handleDuplicate(presentationId: string) {
        withProgressDialog({
            title: "Duplicating presentation...",
            action: async () => {
                try {
                    const duplicatedPresentation = await this.duplicatePresentation({ presentationId, libraryFilter: this.libraryFilter });
                    AppController.showEditor({ presentationId: duplicatedPresentation.id });
                } catch (err) {
                    ShowErrorDialog({ title: "Unable to duplicate presentation(s)", message: err.message });
                }
            }
        });
    }

    public async duplicatePresentation(options: {
        presentationId: string;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const { presentationId, libraryFilter } = options;

        const presentation = await getPresentation(presentationId, { permission: "read", autoSync: false });
        const workspaceId = presentation.getWorkspaceId();

        // Pass the source name through as-is; the server appends a "(N)" suffix when needed
        // so the copy is unique against every presentation in the workspace, not just the
        // records currently loaded in the library pane.
        const { presentationData } = await presentationsApi.copyPresentation({
            id: presentationId,
            workspaceId,
            name: presentation.get("name")
        });

        presentation.disconnect();

        const duplicatedPresentation = await getPresentationPreloaded(presentationData, workspaceId, { permission: "write", autoSync: true });

        if (libraryFilter) {
            await movePresentationToLibraryFilter(duplicatedPresentation, libraryFilter);
        }

        // Ensure the resource is added to state (in case the real time update comes with a delay)
        this._runSequentially(() => {
            if (!this._state.initialized) {
                return;
            }
            return this._forceRefreshResource(duplicatedPresentation.id);
        }).catch(err => {
            logger.error(err, "[PresentationLibraryPaneController] duplicatePresentation() failed to refresh resource", { presentationId: duplicatedPresentation.id });
        });

        return duplicatedPresentation;
    }

    public async createPresentationFromTemplate(options: {
        templatePresentationId: string;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const { templatePresentationId, libraryFilter } = options;

        const duplicatedPresentation = await this.duplicatePresentation({
            presentationId: templatePresentationId,
            libraryFilter
        });

        duplicatedPresentation.update({ isTemplate: false });
        await duplicatedPresentation.updatePromise;

        return duplicatedPresentation;
    }

    public async createPresentationFromStarterTemplate(options: {
        starterTemplateId: string;
        workspaceId: string;
        libraryFilter?: PresentationLibraryFilter;
        addToRecent?: boolean;
    }) {
        const { starterTemplateId, workspaceId, libraryFilter, addToRecent = false } = options;

        const { presentationData } = await presentationsApi.copyPresentation({
            id: starterTemplateId,
            workspaceId,
            preserveCustomFontsFromTemplateFlow: true
        });

        const duplicatedPresentation = await getPresentationPreloaded(presentationData, workspaceId, { permission: "write", autoSync: true });

        if (libraryFilter) {
            await movePresentationToLibraryFilter(duplicatedPresentation, libraryFilter);
        }

        if (addToRecent) {
            const user = AppController.user;
            await user.addPresentationsToRecent([duplicatedPresentation.id]);
        }

        return duplicatedPresentation;
    }

    public async createPresentationFromPPTImport(options: {
        presentationId: string;
        libraryFilter?: PresentationLibraryFilter;
    }) {
        const { presentationId, libraryFilter } = options;
        const createdPresentation = await getPresentation(presentationId, { autoSync: true, permission: "write" });

        // Ensure the slide references are updated
        if (!createdPresentation.get("slideRefs")) {
            await new Promise(resolve => createdPresentation.once("change:slideRefs", resolve));
        }

        if (libraryFilter) {
            await movePresentationToLibraryFilter(createdPresentation, libraryFilter);
        }

        return createdPresentation;
    }

    public async playPresentation(presentationId: string, goBackToLibrary: boolean = false) {
        const presentation = await getPresentation(presentationId, { permission: "read", autoSync: false });
        const currentSlideCount = await getSlidesInPresentationsCount(presentation.getWorkspaceId()).catch(() => 0);

        const entry_slide_index = 0;
        trackActivity("Presentation", "Present");
        const props = {
            initiated_via: "library",
            entry_slide_index: entry_slide_index + 1,
            presentation_id: presentation.id,
            presentation_slide_count: presentation.getSlideCount(),
            current_slide_count: currentSlideCount,
            workspace_id: presentation.getWorkspaceId()
        };
        trackActivity("Presentation", "PresentOpen", null, null, props, { audit: true });

        if (window.roomID) {
            if (!presentation.get("public")) {
                if (await ShowConfirmationDialog({
                    title: "Do you want to set the presentation to public?",
                    message: "Your presentation is set to private but must be public in order to launch using remote control.",
                    okButtonLabel: "Set to public"
                })) {
                    await presentation.setPrivacySetting(PresentationPrivacyType.PUBLIC);
                }
            }
        }

        if (app.integrator) {
            app.integrator.registerPresentation(presentation);
            return;
        }

        if (app.isMobileOrTablet) {
            // To load the player UI to be compatible for mobile we need to switch the location to the player url instead
            // of playing the presentation from app.mainView.playPresentation
            let url = `/player/${presentation.id}`;
            if (window.roomID) {
                url += `?roomID=${window.roomID}&remoteRole=leader`;

                // for efficiency, trigger the registerLeader event on the pusher channel so that the clients can start loading their players immediately
                // instead of needing to wait for our player redirect to load
                const remoteSetupChannel = await pusher.subscribe(`presence-remote-control-${window.roomID}`);
                remoteSetupChannel.trigger("client-registerLeader", { presentationId: presentation.id });
            }
            window.location.href = url;
            return;
        }

        if (window.roomID) {
            // @ts-ignore
            window.remoteRole = "leader";
        }

        AppController.playPresentation({ presentationId: presentation.id, slideIndex: entry_slide_index }, { goBackToLibrary });
    }

    public async convertToPresentationTemplate(
        presentation: IPresentationStorageModel,
        resourceOptions?: { tags?: string[], metadata: Partial<IWorkspaceResourcePresentationTemplate["metadata"]> },
        openEditor?: boolean
    ) {
        return withProgressDialog(
            {
                title: "Creating template...",
                action: async () => {
                    try {
                        await presentation.load();

                        const defaultMetadata: IWorkspaceResourcePresentationTemplate["metadata"] = {
                            description: "",
                            name: "",
                            isTemplateConfirmed: true,
                            isPublished: true,
                            ratedByUsers: [],
                            slideCount: 0,
                            sharedThemeId: null
                        };

                        const presentationMetadata: Partial<IWorkspaceResourcePresentationTemplate["metadata"]> = {
                            name: presentation.get("name"),
                            slideCount: presentation.get("slideRefs") ? Object.keys(presentation.get("slideRefs")).length : undefined,
                            sharedThemeId: presentation.get("sharedThemeId")
                        };

                        await WorkspaceController.createWorkspaceResource({
                            resource: {
                                resourceType: WorkspaceResourceType.PRESENTATION_TEMPLATE,
                                resourceId: presentation.id,
                                tags: resourceOptions?.tags ?? [],
                                folderIds: [],
                                metadata: _.defaults(resourceOptions?.metadata ?? {}, presentationMetadata, defaultMetadata)
                            },
                            defaultPermissions: "shared"
                        });

                        await presentation.reload();

                        if (openEditor) {
                            AppController.showEditor({ presentationId: presentation.id });
                        }

                        return presentation;
                    } catch (err) {
                        logger.error(err, "PresentationLibraryPaneController convertToPresentationTemplate() failed", { presentationId: presentation.id });

                        ShowErrorDialog({
                            title: "Error",
                            message: "Failed to create template. Please try again."
                        });
                    }
                }
            }
        );
    }

    public async updatePresentationTemplate(
        presentation: IPresentationStorageModel,
        resourceOptions?: { tags?: string[], metadata: Partial<Omit<IWorkspaceResourcePresentationTemplate["metadata"], "ratedByUsers">> }
    ) {
        return withProgressDialog(
            {
                title: "Saving template...",
                action: async () => {
                    try {
                        await presentation.load();

                        await WorkspaceController.updateWorkspaceResource({
                            resourceType: WorkspaceResourceType.PRESENTATION_TEMPLATE,
                            resourceId: presentation.id,
                            tags: resourceOptions?.tags ?? undefined,
                            metadata: resourceOptions?.metadata ?? undefined
                        });

                        await presentation.reload();

                        return presentation;
                    } catch (err) {
                        logger.error(err, "PresentationLibraryPaneController updatePresentationTemplate() failed", { presentationId: presentation.id });

                        ShowErrorDialog({
                            title: "Error",
                            message: "Failed to save template. Please try again."
                        });
                    }
                }
            }
        );
    }

    public async trashPresentations(presentationIds: string[]) {
        const { workspaceId } = this._state;

        const presentations = await Promise.all(presentationIds.map(id => getPresentation(id, { permission: "write", autoSync: false })));

        const disconnect = () => presentations.forEach(p => p.disconnect());

        if (presentations.some(p => !p.permissions.owner)) {
            ShowWarningDialog({
                title: "Oops!",
                message: "Presentations that are being collaborated on can not move to trash!",
                acceptCallback: () => { }
            });
            disconnect();
            return;
        }

        const resources = await workspaceResourcesApi.getWorkspaceResources({
            workspaceId,
            resourceTypes: [WorkspaceResourceType.PRESENTATION],
            resourceFilters: [{ field: "resourceId", operator: "in", value: presentationIds }]
        }) as IWorkspaceResourcePresentation[];

        // This will remove all collaborators from the presentations
        await Promise.all(presentations.map(p =>
            WorkspaceController.updateWorkspaceResourceUsers({
                resourceId: p.id,
                resourceType: WorkspaceResourceType.PRESENTATION,
                update: {
                    // Replace with empty array == remove all
                    replace: []
                }
            }))
        );

        const user = AppController.user;

        await Promise.all(presentations.map(async presentation => {
            const resource = resources.find(r => r.resourceId === presentation.id);
            if (resource?.folderIds.length) {
                await Promise.all(resource.folderIds.map(folderId =>
                    WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId,
                        update: {
                            remove: [{ resourceId: presentation.id, resourceType: WorkspaceResourceType.PRESENTATION }]
                        }
                    })
                ));
            }

            await presentation.setPrivacySetting(PresentationPrivacyType.PRIVATE);

            presentation.update({ softDeletedAt: new Date().getTime() });
            await presentation.updatePromise;

            if (user.get("isGDriveEnabled")) {
                gDrive.removePresentation(presentation.id)
                    .catch(err => {
                        logger.error(err, "PresentationLibraryPaneController gDrive.removePresentation() failed", { presentationId: presentation.id });
                    });
            }

            const currentFilter = this.libraryFilter;
            const eventProps = {
                "presentation_id": presentation.id,
                "workspace_id": presentation.getWorkspaceId(),
                "library_location": currentFilter.type,
                "current_folder_id": currentFilter.folderId
            };
            trackActivity("Presentation", "MoveToTrash", null, null, eventProps, { audit: true });
        }));

        disconnect();

        // Fire and forget
        this.forceRefreshPresentations(presentationIds)
            .catch(err => {
                logger.error(err, "PresentationLibraryPaneController forceRefreshPresentations() failed", { presentationIds });
            });
    }

    public async recoverPresentations(presentationIds: string[]) {
        await Promise.all(presentationIds.map(async id => {
            const presentation = await getPresentation(id, { permission: "write", autoSync: false });

            if (!presentation.get("softDeletedAt")) {
                presentation.disconnect();
                return;
            }

            presentation.update({ softDeletedAt: null });
            await presentation.updatePromise;

            const eventProps = {
                "presentation_id": presentation.id,
                "library_location": this.libraryFilter.type,
            };
            trackActivity("Presentation", "Recovered", null, null, eventProps, { audit: true });

            const user = AppController.user;
            if (user.get("isGDriveEnabled")) {
                gDrive.savePresentation(presentation.id)
                    .catch(err => {
                        logger.error(err, "PresentationLibraryPaneController gDrive.savePresentation() failed", { presentationId: presentation.id });
                    });
            }

            presentation.disconnect();
        }));

        // Fire and forget
        this.forceRefreshPresentations(presentationIds)
            .catch(err => {
                logger.error(err, "PresentationLibraryPaneController forceRefreshPresentations() failed", { presentationIds });
            });
    }

    public async deletePresentations(presentationIds: string[]) {
        await withMaxConcurrency(presentationIds.map(presentationId => async () => {
            const presentation = await getPresentation(presentationId, { permission: "read", autoSync: false });

            const workspacePermissions = await getWorkspacePermissionsToResource({
                workspaceId: presentation.getWorkspaceId(),
                resourceId: presentationId,
                resourceType: WorkspaceResourceType.PRESENTATION
            });
            const hasDirectPermission = workspacePermissions
                .some(({ subjectId, subjectType }) => subjectType === WorkspacePermissionSubjectType.USER && subjectId === app.user.id);

            if (!hasDirectPermission) {
                throw new DirectPermissionRequiredError("Permission to this presentation cannot be deleted as it's shared via folder. Leave the folder instead.");
            }

            await presentationsApi.deletePresentation({ id: presentationId });
            await this._forceRefreshResource(presentationId);

            // Legacy analytics
            const deletedSlideCount = -Object.keys(presentation.get("slideRefs") || {}).length;
            const workspaceId = presentation.getWorkspaceId();
            const currentSlideCount = await getSlidesInPresentationsCount(workspaceId).catch(() => 0);

            const eventProps = {
                "presentation_id": presentationId,
                "library_location": this.libraryFilter.type,
                "current_slide_count": currentSlideCount,
                "slides_created": deletedSlideCount,
            };
            trackActivity("Presentation", "Delete", null, null, eventProps, { audit: true });
            ////
        }), 5);
    }
}

const presentationLibraryPaneController = new PresentationLibraryPaneController();

// For lazy import, debug, and analytics helpers.
app.presentationLibraryPaneController = presentationLibraryPaneController;

export default presentationLibraryPaneController;
